import { useAuth0 } from '@auth0/auth0-react';
import { useCheck } from '@my/check-api/hooks';
import { Button, ErrorBanner, Loading, PageHeader } from '@my/ui';
import { ArrowLeft, Camera, PenLine, TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { AnswerPhoto, CheckSignature } from './components/CheckAttachments';
import {
  ANSWER_BADGE,
  DEFECT_STATUS_BADGE,
  OUTCOME_BADGE,
  OUTCOME_DOT,
  SEVERITY_BADGE,
  STATUS_BADGE,
  answerSections,
  formatWhen,
  sectionLabelFrom,
} from './lib/checkDisplay';

// ────────────────────────────────────────────────────────────────────────────
// One recorded check, read-only. This is the compliance record — a manager
// reads it, an auditor reads it, nobody edits it.
//
// Question wording comes from the template version pinned on the check, which
// is immutable once published, so a two-year-old record still shows the words
// the driver actually saw.
// ────────────────────────────────────────────────────────────────────────────

export function CheckDetailPage() {
  const { t } = useTranslation('checks');
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth0();
  const navigate = useNavigate();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);

  const query = useCheck(id ?? null, orgId);

  if (query.isPending) {
    return (
      <Shell onBack={() => navigate('/checks')}>
        <Loading label={t('detail.title')} />
      </Shell>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Shell onBack={() => navigate('/checks')}>
        <ErrorBanner title={t('detail.loadFailed')} message={query.error?.message ?? ''} />
      </Shell>
    );
  }

  const { summary: s, answers, defects, sessionSiblings } = query.data;
  const sections = answerSections(answers);

  return (
    <Shell onBack={() => navigate('/checks')}>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 pb-4 lg:grid-cols-[1fr_minmax(260px,320px)]">
        {/* ── Answers ─────────────────────────────────────────────────── */}
        <div className="min-h-0 space-y-4 lg:h-full lg:overflow-y-auto">
          {sections.map((sectionKey) => {
            const rows = answers.filter((a) => a.sectionKey === sectionKey);
            const failed = rows.filter((a) => a.answer === 'Defect').length;
            return (
              <section key={sectionKey}>
                <div className="mb-1.5 flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    {sectionLabelFrom(answers, sectionKey)}
                  </h3>
                  <span className="rounded-full bg-slate-200 px-1.5 text-[10px] font-medium text-slate-600">
                    {rows.length}
                  </span>
                  {failed > 0 && (
                    <span className="rounded-full bg-red-100 px-1.5 text-[10px] font-medium text-red-700">
                      {t('detail.failedInSection', { count: failed })}
                    </span>
                  )}
                </div>
                <ul className="space-y-1.5">
                  {rows.map((a) => (
                    <li
                      key={a.id}
                      className="relative overflow-hidden rounded-sm border border-slate-300 bg-white px-2 py-1.5 pl-4"
                    >
                      {/* Stripe carries the answer at a glance down the page. */}
                      <span
                        className={`absolute inset-y-0 left-0 w-1 ${
                          a.answer === 'Defect'
                            ? 'bg-red-500'
                            : a.answer === 'NotApplicable'
                              ? 'bg-amber-400'
                              : 'bg-emerald-500'
                        }`}
                        aria-hidden="true"
                      />
                      <div className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 text-xs text-slate-800">{a.label}</span>
                        {a.hasPhoto && (
                          <span
                            title={a.photoFileName ?? t('detail.photoAttached')}
                            className="flex-shrink-0 text-slate-400"
                          >
                            <Camera className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <span
                          className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${ANSWER_BADGE[a.answer]}`}
                        >
                          {t(
                            a.answer === 'Ok'
                              ? 'detail.answer.pass'
                              : a.answer === 'Defect'
                                ? 'detail.answer.fail'
                                : 'detail.answer.na',
                          )}
                        </span>
                      </div>
                      {a.note && (
                        <p className="mt-1 text-[11px] text-slate-600">{a.note}</p>
                      )}
                      {a.hasPhoto && id && <AnswerPhoto checkId={id} answerId={a.id} />}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}

          {answers.length === 0 && (
            <p className="py-6 text-center text-xs italic text-slate-400">
              {t('detail.noAnswers')}
            </p>
          )}
        </div>

        {/* ── Summary rail ────────────────────────────────────────────── */}
        <aside className="min-h-0 space-y-3 lg:h-full lg:overflow-y-auto">
          <Panel title={t('detail.summaryTitle')}>
            <Row label={t('list.grid.vehicle')}>
              <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-700">
                {s.resourceRegistration}
              </span>
            </Row>
            <Row label={t('list.grid.driver')}>{s.performedByName}</Row>
            <Row label={t('detail.started')}>{formatWhen(s.startedUtc)}</Row>
            <Row label={t('detail.completed')}>{formatWhen(s.completedUtc)}</Row>
            <Row label={t('list.grid.odometer')}>
              {s.odometerKm != null ? `${s.odometerKm.toLocaleString()} km` : '—'}
            </Row>
            <Row label={t('list.grid.template')}>
              {s.templateName}
              <span className="ml-1 text-[10px] text-slate-400">v{s.templateVersion}</span>
            </Row>
            <Row label={t('detail.answersLabel')}>
              {t('detail.answersValue', {
                total: s.answerCount,
                failed: s.failedCount,
                na: s.notApplicableCount,
              })}
            </Row>
          </Panel>

          {/* Declaration is what makes this an RSA / DVSA record rather than a
              list of taps, so it gets its own panel rather than a summary row. */}
          <Panel title={t('detail.declarationTitle')}>
            {query.data.declarationAccepted ? (
              <p className="flex items-start gap-1.5 text-xs text-slate-700">
                <PenLine className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600" />
                <span>
                  {t('detail.declarationAccepted', {
                    when: formatWhen(query.data.declarationUtc),
                  })}
                  {query.data.hasSignature && ` · ${t('detail.signatureOnFile')}`}
                </span>
              </p>
            ) : (
              <p className="flex items-start gap-1.5 text-xs text-amber-800">
                <TriangleAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                {t('detail.declarationMissing')}
              </p>
            )}
            {query.data.hasSignature && id && <CheckSignature checkId={id} />}
          </Panel>

          {defects.length > 0 && (
            <Panel title={t('detail.defectsTitle', { count: defects.length })}>
              <ul className="space-y-1.5">
                {defects.map((d) => (
                  <li key={d.id} className="rounded-sm border border-slate-200 bg-slate-50 p-2">
                    <p className="text-xs font-medium text-slate-800">{d.title}</p>
                    <p className="mt-1 flex items-center gap-1">
                      <span
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${SEVERITY_BADGE[d.severity]}`}
                      >
                        {d.severity}
                      </span>
                      <span
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${DEFECT_STATUS_BADGE[d.status]}`}
                      >
                        {d.status}
                      </span>
                    </p>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* The rest of the same walk-around — typically the coupled trailer.
              Worth surfacing: the unit can pass while the trailer fails. */}
          {sessionSiblings.length > 0 && (
            <Panel title={t('detail.siblingsTitle')}>
              <ul className="space-y-1">
                {sessionSiblings.map((sib) => (
                  <li key={sib.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/checks/${sib.id}`)}
                      className="flex w-full items-center gap-2 rounded-sm px-1 py-1 text-left hover:bg-slate-100"
                    >
                      <span
                        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${OUTCOME_DOT[sib.outcome]}`}
                        aria-hidden="true"
                      />
                      <span className="font-mono text-[11px] text-slate-700">
                        {sib.resourceRegistration}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </Panel>
          )}
        </aside>
      </div>
    </Shell>
  );

  function Shell({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
    return (
      <div className="flex h-full min-h-0 flex-col px-2">
        <PageHeader title={t('detail.title')} description={t('detail.description')} />
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
          <Button variant="ghost" size="xs" leftIcon={ArrowLeft} onClick={onBack}>
            {t('detail.backToList')}
          </Button>
          {query.data && (
            <>
              <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[11px] font-medium text-slate-700">
                {query.data.summary.resourceRegistration}
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${OUTCOME_BADGE[query.data.summary.outcome]}`}
              >
                {t(
                  `list.outcome.${query.data.summary.outcome.toLowerCase()}` as 'list.outcome.pass',
                )}
              </span>
              <span
                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_BADGE[query.data.summary.status]}`}
              >
                {t(
                  query.data.summary.status === 'InProgress'
                    ? 'list.status.inProgress'
                    : 'list.status.submitted',
                )}
              </span>
            </>
          )}
        </div>
        <main className="flex min-h-0 flex-1 flex-col pt-3">{children}</main>
      </div>
    );
  }
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-sm border border-slate-300 bg-white p-3 shadow-sm">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
        {label}
      </span>
      <span className="min-w-0 truncate text-right text-xs text-slate-700">{children}</span>
    </div>
  );
}
